import { useEffect, useState } from 'react';

import { estimatePasswordStrengthHeuristic } from '@tankef/core';

import { cn } from '../../utils';

type ZxcvbnFn = (pw: string) => { score: 0 | 1 | 2 | 3 | 4 };
let cachedZxcvbn: ZxcvbnFn | null = null;

/**
 * 3-level strength meter (UX: simple > detailed). We still compute on the
 * zxcvbn 0–4 scale internally for accuracy, then collapse to Débil / Aceptable /
 * Fuerte for display.
 *
 * We lazy-load zxcvbn-ts on first render to keep the auth bundle lean — only
 * users that see a meter actually pay the ~80KB cost. While zxcvbn warms up,
 * the heuristic fallback provides a reasonable estimate.
 */
export interface PasswordStrengthMeterProps {
  password: string;
  /** i18n-resolved labels. */
  labels: {
    weak: string;
    fair: string;
    strong: string;
  };
  /**
   * If true, shows a single non-technical hint at a time (the highest-priority
   * improvement for the current password). Defaults to `true`.
   */
  showHint?: boolean;
  /** i18n-resolved hint copy. Only rendered when `showHint` is true. */
  hintLabels?: {
    addLength: string;
    addUppercase: string;
    addLowercase: string;
    addDigit: string;
    addSymbol: string;
    avoidCommon: string;
  };
  className?: string;
}

type Level = 'weak' | 'fair' | 'strong';

/**
 * Collapse zxcvbn's 0–4 ordinal score into 3 tiers. We keep `weak` for 0–1
 * (short/obvious), `fair` for 2 (acceptable), `strong` for 3–4.
 */
function scoreToLevel(score: 0 | 1 | 2 | 3 | 4): Level {
  if (score <= 1) return 'weak';
  if (score === 2) return 'fair';
  return 'strong';
}

const levelStyles: Record<Level, { bar: string; label: string }> = {
  weak: { bar: 'bg-error-500', label: 'text-tkf-fg-error' },
  fair: { bar: 'bg-warning-500', label: 'text-tkf-fg-warning' },
  strong: { bar: 'bg-success-500', label: 'text-tkf-fg-success' },
};

export function PasswordStrengthMeter({
  password,
  labels,
  showHint = true,
  hintLabels,
  className,
}: PasswordStrengthMeterProps) {
  const [zxcvbnScore, setZxcvbnScore] = useState<0 | 1 | 2 | 3 | 4 | null>(null);
  const [zxcvbnReady, setZxcvbnReady] = useState(false);

  // Lazy load zxcvbn-ts once. The initial heuristic covers the gap so nothing
  // flickers — we just refine the score when the real estimator arrives.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ zxcvbn, zxcvbnOptions }, common, es, en] = await Promise.all([
        import('@zxcvbn-ts/core'),
        import('@zxcvbn-ts/language-common'),
        import('@zxcvbn-ts/language-es-es'),
        import('@zxcvbn-ts/language-en'),
      ]);
      if (cancelled) return;
      zxcvbnOptions.setOptions({
        dictionary: {
          ...common.dictionary,
          ...en.dictionary,
          ...es.dictionary,
        },
        graphs: common.adjacencyGraphs,
        translations: es.translations,
      });
      cachedZxcvbn = zxcvbn as ZxcvbnFn;
      setZxcvbnReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!password) {
      setZxcvbnScore(null);
      return;
    }
    if (zxcvbnReady && cachedZxcvbn) {
      setZxcvbnScore(cachedZxcvbn(password).score);
    }
  }, [password, zxcvbnReady]);

  const heuristicScore = estimatePasswordStrengthHeuristic(password);
  const score = zxcvbnScore ?? heuristicScore;
  const level = password ? scoreToLevel(score) : 'weak';
  const styles = levelStyles[level];

  // Segmented bar: 3 segments, filled progressively.
  const filledSegments = !password ? 0 : level === 'weak' ? 1 : level === 'fair' ? 2 : 3;

  // Single prioritized hint — driven by PASSWORD_RULES, not strength score.
  // All rules met + no common patterns = null (naturally no hint, button enables).
  const hint = (() => {
    if (!password || !showHint || !hintLabels) return null;
    if (password.length < 12) return hintLabels.addLength;
    if (!/[A-Z]/.test(password)) return hintLabels.addUppercase;
    if (!/[a-z]/.test(password)) return hintLabels.addLowercase;
    if (!/\d/.test(password)) return hintLabels.addDigit;
    if (!/[^A-Za-z0-9]/.test(password)) return hintLabels.addSymbol;
    if (score <= 1) return hintLabels.avoidCommon;
    return null;
  })();

  return (
    <div className={cn('flex flex-col gap-1', className)} aria-live="polite">
      <div className="flex items-center gap-2">
        <div className="flex h-1 flex-1 gap-1">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={cn(
                'flex-1 rounded-full transition-colors duration-300',
                i < filledSegments ? styles.bar : 'bg-tkf-surface-muted'
              )}
            />
          ))}
        </div>
        {password && (
          <span className={cn('text-[11px] font-semibold uppercase tracking-wider', styles.label)}>
            {labels[level]}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-tkf-fg-error">{hint}</p>}
    </div>
  );
}
